import { PageHeader } from "@dmca/frontend/components/nav";
import { Button } from "@dmca/frontend/components/button";
import { Modal } from "@dmca/frontend/components/modal";
import { useAppPermissions } from "@dmca/frontend/hooks";
import { useTRPC } from "@dmca/frontend/trpc";
import {
  HelpSections,
  HelpEntryPdfMaxSizeBytes,
  HelpEntryPdfMaxSizeMessage,
} from "@dmca/models/interface/help";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/help/$section")({
  component: HelpSectionPage,
  loader: async ({ context, params }) => {
    const { trpcQcProxy: trpc, qc } = context;
    const section = HelpSections.find((item) => item.id === params.section);
    if (!section) {
      throw notFound();
    }
    await qc.ensureQueryData(
      trpc.help.getPage.queryOptions({ section: section.id })
    );
  },
  head: () => ({
    meta: [{ title: "Help | DNA Dashboard" }],
  }),
});

function HelpSectionPage() {
  const { section: sectionId } = Route.useParams();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasPermission = useAppPermissions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState("");
  const [previewEntryId, setPreviewEntry] = useState<string>();
  const [preview, setPreview] = useState<{ fileName: string; url: string }>();

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview]
  );

  const section = HelpSections.find((item) => item.id === sectionId);
  if (!section) {
    throw notFound();
  }

  const canManage = hasPermission(section.managePermission);
  const { data: page } = useQuery(
    trpc.help.getPage.queryOptions({ section: section.id })
  );

  const addEntryMutation = useMutation(
    trpc.help.addEntry.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to add help entry.");
      },
      onSuccess: () => {
        toast.success("Help entry added.");
        setDescription("");
        qc.invalidateQueries(
          trpc.help.getPage.queryFilter({ section: section.id })
        );
        qc.invalidateQueries(trpc.help.getPages.queryFilter());
      },
    })
  );

  const createPageMutation = useMutation(
    trpc.help.createPage.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to create help page");
      },
      onSuccess: () => {
        toast.success("Help page created.");
        qc.invalidateQueries(
          trpc.help.getPage.queryFilter({ section: section.id })
        );
        qc.invalidateQueries(trpc.help.getPages.queryFilter());
      },
    })
  );

  const deleteEntryMutation = useMutation(
    trpc.help.deleteEntry.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to delete help entry.");
      },
      onSuccess: () => {
        toast.success("Help entry deleted.");
        qc.invalidateQueries(
          trpc.help.getPage.queryFilter({ section: section.id })
        );
        qc.invalidateQueries(trpc.help.getPages.queryFilter());
      },
    })
  );

  const deletePageMutation = useMutation(
    trpc.help.deletePage.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to delete help page.");
      },
      onSuccess: () => {
        toast.success("Help page deleted.");
        qc.invalidateQueries(
          trpc.help.getPage.queryFilter({ section: section.id })
        );
        qc.invalidateQueries(trpc.help.getPages.queryFilter());
        navigate({ to: "/help/add-new" });
      },
    })
  );

  const handleAddEntryClick = () => {
    if (!description.trim()) {
      toast.error("Enter a description before adding a PDF.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (file.size > HelpEntryPdfMaxSizeBytes) {
      toast.error(HelpEntryPdfMaxSizeMessage);
      return;
    }

    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      toast.error("Help entries must use a PDF file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const base64 = (readerEvent.target?.result as string | undefined)?.split(
        ","
      )[1];
      if (!base64) {
        toast.error("Unknown error reading the file.");
        return;
      }
      addEntryMutation.mutate({
        section: section.id,
        description: description.trim(),
        fileName: file.name,
        contentType: file.type || "application/pdf",
        file: base64,
      });
    };
    reader.onerror = () => {
      toast.error("Unknown error reading the file.");
    };
    reader.readAsDataURL(file);
  };

  const handlePreviewEntry = async (entryId: string) => {
    setPreviewEntry(entryId);
    try {
      const { data, contentType, fileName } = await qc.fetchQuery(
        trpc.help.downloadEntry.queryOptions({
          section: section.id,
          entryId,
        })
      );
      const bytes = Uint8Array.from(atob(data), (char) => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
      setPreview({ fileName, url });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to preview help entry"
      );
    } finally {
      setPreviewEntry(undefined);
    }
  };

  return (
    <Container>
      <PageHeader>{page?.title ?? section.label}</PageHeader>
      <Stack gap={2} sx={{ mt: 2 }}>
        {canManage && page && (
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <Box
                component="input"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                placeholder="Entry description"
                style={{
                  flex: 1,
                  minHeight: 40,
                  padding: "0 12px",
                }}
              />
              <Button
                loading={addEntryMutation.isPending}
                onClick={handleAddEntryClick}
              >
                Add Entry
              </Button>
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFileChange}
              />
            </Stack>
          </Paper>
        )}

        {!page && (
          <Paper sx={{ p: 2 }}>
            <Stack gap={1} alignItems="flex-start">
              <Typography>This help page has not been created yet.</Typography>
              {canManage && (
                <Button
                  loading={createPageMutation.isPending}
                  onClick={() =>
                    createPageMutation.mutate({ section: section.id })
                  }
                >
                  Create Help Page
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {page && page.entries.length === 0 && (
          <Paper sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography>No help entries have been added yet.</Typography>
              {canManage && (
                <Button
                  variant="outlined"
                  loading={deletePageMutation.isPending}
                  onClick={() =>
                    deletePageMutation.mutate({ section: section.id })
                  }
                >
                  Delete Help Page
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {page && page.entries.length > 0 && (
          <List component={Paper}>
            {page.entries.map((entry) => (
              <ListItem
                key={entry.id}
                disablePadding
                secondaryAction={
                  canManage && (
                    <Tooltip title="Delete Entry">
                      <IconButton
                        edge="end"
                        aria-label={`Delete ${entry.description}`}
                        disabled={deleteEntryMutation.isPending}
                        onClick={() =>
                          deleteEntryMutation.mutate({
                            section: section.id,
                            entryId: entry.id,
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <Box
                  component="button"
                  sx={{
                    background: "transparent",
                    border: 0,
                    color: "text.primary",
                    cursor: "pointer",
                    font: "inherit",
                    textAlign: "left",
                    width: "100%",
                    px: 2,
                    py: 1.5,
                    pr: canManage ? 7 : 2,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  disabled={previewEntryId === entry.id}
                  onClick={() => void handlePreviewEntry(entry.id)}
                >
                  <ListItemText
                    primary={entry.description}
                    secondary={entry.fileName}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
      <Modal
        open={Boolean(preview)}
        onClose={() => setPreview(undefined)}
        header={preview?.fileName}
        fullWidth
        sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      >
        {preview && (
          <Box
            component="iframe"
            src={preview.url}
            title={`Preview ${preview.fileName}`}
            sx={{ border: 0, flex: 1, width: "100%" }}
          />
        )}
      </Modal>
    </Container>
  );
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
